import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch } from '@/modules/core/composables/useFetch'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'

interface Onboarding {
  current_step: string
  completed_at: string | null
  is_complete: boolean
  steps: string[]
}

interface Workspace {
  id: number
  name: string
  subdomain: string
  phone: string | null
  time_zone: string
  default_currency_code: string
  setup_mode: boolean
  logo_url: string | null
  is_registered_investment_advisor: boolean
  central_registration_depository_number: string | null
  accreditation_country_code: string | null
  accreditation_level_min: string | null
  is_investment_group: boolean
  connecting_candidate_id: number | null
  website: string | null
}

interface Settings {
  deal_pipeline_enabled: boolean
  secondary_deal_pipeline_enabled: boolean
  funding_application_enabled: boolean
  accepts_shared_deals: boolean
  public_investor_registration_enabled: boolean
  hide_investors_from_investors: boolean
  show_chapters_to_investors: boolean
  skip_self_accreditation: boolean
  send_investor_registration_reminders: boolean
  company_index_enabled: boolean
  company_discussions_enabled: boolean
  all_investors_view_captable: boolean
  investor_aml_verification_enabled: boolean
  send_past_due_invoice_reminders: boolean
  send_past_due_signature_reminders: boolean
  sort_discussion_newest_to_oldest: boolean
  link_to_email_attachments: boolean
  notify_when_investor_registers: boolean
  notify_when_company_applies: boolean
  notify_when_company_begins: boolean
}

interface TimezoneOption {
  value: string
  label: string
}

interface AccreditationCountryOption {
  value: string
  label: string
}

interface Options {
  timezones: TimezoneOption[]
  currencies: string[]
  accreditation_countries: AccreditationCountryOption[]
}

interface OnboardingResponse {
  onboarding: Onboarding
  group: Workspace
  settings: Settings
  options: Options
}

export const useWorkspaceOnboardingStore = defineStore('workspace/onboardingStore', () => {
  const route = useRoute()
  const notificationStore = useNotificationStore()

  const baseUrl = computed(() => `/v4/${route.params.slug}/workspace/onboarding`)

  // State
  const onboarding = ref<Onboarding | null>(null)
  const workspace = ref<Workspace | null>(null)
  const settings = ref<Settings | null>(null)
  const options = ref<Options | null>(null)
  const loading = ref(false)

  // Actions
  const fetch = async () => {
    loading.value = true
    try {
      const data = await useFetch(baseUrl.value).get<OnboardingResponse>()
      onboarding.value = data.onboarding
      workspace.value = data.group
      settings.value = data.settings
      options.value = data.options
      return true
    } catch (error) {
      notificationStore.enqueue('error', 'Failed to load onboarding data')
      return false
    } finally {
      loading.value = false
    }
  }

  const advanceStep = async (currentStep: string) => {
    loading.value = true
    try {
      const data = await useFetch(`${baseUrl.value}/advance_step`).post<OnboardingResponse>({
        current_step: currentStep,
      })
      onboarding.value = data.onboarding
      workspace.value = data.group
      settings.value = data.settings
      options.value = data.options
      return true
    } catch (error) {
      notificationStore.enqueue('error', 'Failed to advance onboarding step')
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    onboarding,
    workspace,
    settings,
    options,
    loading,
    fetch,
    advanceStep,
  }
})
